import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Combobox } from "@/components/combobox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCurrentUser } from "@/hooks/use-current-user";

import type { ClaimWithRelations } from "../api/use-claim";
import { type ClaimDocumentType, typeForAgent, typeTranslations } from "../lib/document-types";

interface ClaimDocumentFieldsProps {
  passengers: ClaimWithRelations["usersClaims"];
  documentType: ClaimDocumentType;
  onDocumentTypeChange: (documentType: ClaimDocumentType) => void;
  /** Null: the whole claim. */
  userClaimId: string | null;
  onUserClaimIdChange: (userClaimId: string | null) => void;
}

/**
 * The « Type de document » picker, the list depending on the role. Shared by « Ajouter un document », the file
 * explorer's uploads and « Changer le type ».
 */
export function ClaimDocumentTypeCombobox({
  value,
  onChange,
}: {
  value: ClaimDocumentType;
  onChange: (documentType: ClaimDocumentType) => void;
}) {
  const { can } = useCurrentUser();
  const documentTypes = can([...UPCLAIM_ROLES, "LAWYER", "PLEADING_LAWYER"])
    ? typeTranslations
    : typeForAgent;
  const typeOptions = (Object.entries(documentTypes) as Array<[ClaimDocumentType, string]>).map(
    ([type, label]) => ({ type, label }),
  );
  const selectedTypeOption = typeOptions.find((option) => option.type === value) ?? null;

  return (
    <Combobox
      items={typeOptions}
      value={selectedTypeOption}
      onChange={(option) => {
        if (option) {
          onChange(option.type);
        }
      }}
      getKey={(option) => option.type}
      getLabel={(option) => option.label}
      placeholder="Sélectionner un type"
      searchPlaceholder="Rechercher un type..."
      emptyMessage="Aucun type trouvé"
    />
  );
}

/**
 * What a claim document is (« Type de document ») and, optionally, which passenger it belongs to. Shared by
 * « Ajouter un document » and the file explorer's uploads.
 */
export function ClaimDocumentFields({
  passengers,
  documentType,
  onDocumentTypeChange,
  userClaimId,
  onUserClaimIdChange,
}: ClaimDocumentFieldsProps) {
  const passengerOptions = passengers.map(({ id, user }) => ({
    id,
    fullName: `${user.firstName} ${user.lastName?.toUpperCase() ?? ""}`,
  }));

  return (
    <>
      <div className="py-2 text-left">
        <Label className="mb-1 block text-sm font-medium text-gray-700">Type de document</Label>
        <ClaimDocumentTypeCombobox value={documentType} onChange={onDocumentTypeChange} />
      </div>

      <div className="py-2 text-left">
        <Label className="mb-1 block text-sm font-medium text-gray-700">
          Rattacher à un passager <span className="text-gray-400">(optionnel)</span>
        </Label>
        <Select
          value={userClaimId ?? "none"}
          onValueChange={(value) => onUserClaimIdChange(value === "none" ? null : String(value))}
        >
          <SelectTrigger className="w-full">
            <SelectValue>
              {userClaimId
                ? (passengerOptions.find((passenger) => passenger.id === userClaimId)?.fullName ?? "—")
                : "Aucun (dossier entier)"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Aucun (dossier entier)</SelectItem>
            {passengerOptions.map((passenger) => (
              <SelectItem key={passenger.id} value={passenger.id}>
                {passenger.fullName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}
